import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Campaign, BlockAAnswer, CampaignAnswer, LevelRunState } from '../types';
import { getBlockATemplateByCurriculum } from '../reflections/blockARegistry';
import QuestionCard from './QuestionCard';
import { ProgressService } from '../services/ProgressService';
import CashBurst from '../../../shared/ui/CashBurst';

interface LevelRunProps {
  levelId: string;
  studentId: string;
  squadId: string;
  sessionId: string;
  campaigns: Campaign[];
  onComplete: (totalEarned: number) => void;
  onCancel: () => void;
}

const LevelRun: React.FC<LevelRunProps> = ({
  levelId,
  studentId,
  squadId,
  sessionId,
  campaigns,
  onComplete,
  onCancel
}) => {
  useEffect(() => {
    console.log('[LevelRun] mount', { levelId, studentId, squadId, sessionId, campaignsCount: campaigns.length });
    return () => {
      console.log('[LevelRun] unmount');
    };
  }, []);
  const [runState, setRunState] = useState<LevelRunState>({
    levelId,
    blockACompleted: false,
    blockBQuestions: [],
    currentQuestionIndex: 0,
    totalEarned: 0,
    answers: []
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCashBurst, setShowCashBurst] = useState(false);
  const [phase, setPhase] = useState<'blockA' | 'blockB' | 'complete'>('blockA');
  const [completionData, setCompletionData] = useState<{
    blockAEarned: number;
    blockBEarned: number;
    totalQuestions: number;
  } | null>(null);

  // Cleanup refs for timeouts
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
  const isMountedRef = useRef(true);

  const [curriculumId, setCurriculumId] = useState<string>('music');
  useEffect(() => {
    // Fetch squad to resolve curriculum; minimal fetch to avoid prop surgery
    const loadSquad = async () => {
      try {
        const { db } = await import('../../../shared/storage/firebase');
        const { doc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(db, 'squads', squadId));
        const data = snap.data();
        const id = (data?.curriculumId || 'music').toString();
        setCurriculumId(id);
      } catch (e) {
        console.warn('[LevelRun] failed to fetch squad for curriculum, defaulting to music', e);
        setCurriculumId('music');
      }
    };
    loadSquad();
  }, [squadId]);

  const blockATemplate = getBlockATemplateByCurriculum(levelId, curriculumId);

  // Flatten all campaign questions
  const allBlockBQuestions = campaigns.flatMap(campaign =>
    campaign.questions.map(q => ({ ...q, id: q.questionId, campaignId: campaign.id! }))
  );

  // Set up cleanup for timeouts on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      // Cleanup any pending timeouts on unmount
      console.log('[LevelRun] cleanup:clearing timeouts', { count: timeoutRefs.current.length });
      isMountedRef.current = false;
      timeoutRefs.current.forEach(timeout => clearTimeout(timeout));
      timeoutRefs.current = [];
    };
  }, []);

  useEffect(() => {
    console.log('[LevelRun] useEffect:campaigns changed', { campaignsCount: campaigns.length });
    const flattened = campaigns.flatMap(campaign => 
      campaign.questions.map(q => ({ ...q, id: q.questionId, campaignId: campaign.id! }))
    );
    console.log('[LevelRun] flattened BlockB questions', { flattenedCount: flattened.length, flattenedIds: flattened.map(q => q.id) });
    setRunState(prev => ({
      ...prev,
      blockBQuestions: flattened
    }));
  }, [campaigns]);

  const handleBlockAAnswer = async (answer: BlockAAnswer) => {
    console.log('[LevelRun] handleBlockAAnswer:start', { answer, currentIndex: runState.currentQuestionIndex, totalEarned: runState.totalEarned });
    const newAnswers = [...runState.answers, answer];
    const newEarned = runState.totalEarned + 20;
    
    setRunState(prev => ({
      ...prev,
      answers: newAnswers,
      totalEarned: newEarned,
      currentQuestionIndex: prev.currentQuestionIndex + 1
    }));
    console.log('[LevelRun] handleBlockAAnswer:stateUpdated', { newEarned, answersCount: newAnswers.length, nextIndex: runState.currentQuestionIndex + 1 });

    // Show cash burst
    setShowCashBurst(true);
    console.log('[LevelRun] handleBlockAAnswer:cashBurst');

    // Check if Block A is complete
    if (blockATemplate && runState.currentQuestionIndex + 1 >= blockATemplate.questions.length) {
      console.log('[LevelRun] handleBlockAAnswer:blockA complete condition met', { currentIndex: runState.currentQuestionIndex + 1, totalQuestions: blockATemplate.questions.length });
      setIsProcessing(true);

      // Small delay to let cash animation start, then immediately transition
      const timeoutId = setTimeout(() => {
        // Guard against unmounted component
        if (!isMountedRef.current) {
          console.warn('[LevelRun] handleBlockAAnswer:timeout fired but component unmounted');
          return;
        }

        console.log('[LevelRun] handleBlockAAnswer:transition after delay');
        setRunState(prev => ({ ...prev, blockACompleted: true }));

        if (allBlockBQuestions.length > 0) {
          console.log('[LevelRun] switching phase to blockB', { blockBCount: allBlockBQuestions.length });
          setPhase('blockB');
          setRunState(prev => ({ ...prev, currentQuestionIndex: 0 }));
        } else {
          // No Block B questions, proceed to completion with the updated answers
          console.log('[LevelRun] no BlockB questions, calling completeReflection');
          completeReflection(newAnswers, newEarned, blockATemplate.questions.length, 0);
        }
        setIsProcessing(false);
        console.log('[LevelRun] handleBlockAAnswer:processing=false');
      }, 100);
      timeoutRefs.current.push(timeoutId);
    }
  };

  const handleBlockBAnswer = async (answer: CampaignAnswer) => {
    console.log('[LevelRun] handleBlockBAnswer:start', { answer, currentIndex: runState.currentQuestionIndex, totalEarned: runState.totalEarned });
    const newAnswers = [...runState.answers, answer];
    const newEarned = runState.totalEarned + 20;
    
    setRunState(prev => ({
      ...prev,
      answers: newAnswers,
      totalEarned: newEarned,
      currentQuestionIndex: prev.currentQuestionIndex + 1
    }));
    console.log('[LevelRun] handleBlockBAnswer:stateUpdated', { newEarned, answersCount: newAnswers.length, nextIndex: runState.currentQuestionIndex + 1 });

    // Show cash burst
    setShowCashBurst(true);
    console.log('[LevelRun] handleBlockBAnswer:cashBurst');

    // Check if Block B is complete
    if (runState.currentQuestionIndex + 1 >= allBlockBQuestions.length) {
      console.log('[LevelRun] handleBlockBAnswer:blockB complete condition met', { nextIndex: runState.currentQuestionIndex + 1, total: allBlockBQuestions.length });
      setIsProcessing(true);

      // Small delay to let cash animation start, then immediately complete
      const timeoutId = setTimeout(() => {
        // Guard against unmounted component
        if (!isMountedRef.current) {
          console.warn('[LevelRun] handleBlockBAnswer:timeout fired but component unmounted');
          return;
        }

        const blockACount = blockATemplate?.questions.length || 0;
        const blockBCount = allBlockBQuestions.length;
        console.log('[LevelRun] handleBlockBAnswer:calling completeReflection', { blockACount, blockBCount });
        completeReflection(newAnswers, newEarned, blockACount, blockBCount);
        setIsProcessing(false);
        console.log('[LevelRun] handleBlockBAnswer:processing=false');
      }, 100);
      timeoutRefs.current.push(timeoutId);
    }
  };

  const completeReflection = useCallback(async (
    finalAnswers: (BlockAAnswer | CampaignAnswer)[],
    _totalEarned: number,
    blockACount: number,
    blockBCount: number
  ) => {
    console.log('[LevelRun] completeReflection:start', {
      studentId,
      squadId,
      sessionId,
      levelId,
      finalAnswersCount: finalAnswers.length,
      expectedBlockA: blockACount,
      expectedBlockB: blockBCount
    });
    setIsLoading(true);
    console.log('[LevelRun] completeReflection:isLoading=true');
    
    try {
      // Separate Block A and Block B answers
      const blockAAnswers: Record<string, BlockAAnswer> = {};
      const blockBAnswers: CampaignAnswer[] = [];
      
      finalAnswers.forEach((answer) => {
        // More reliable detection: check the structure of the answer
        const hasBlockBFields = 'campaignId' in answer && 'type' in answer && 'answer' in answer;
        const hasBlockAFields = 'questionId' in answer && ('choiceId' in answer || 'emojiValue' in answer);
        
        if (hasBlockBFields) {
          // This is a Block B (campaign) answer
          const campaignAnswer = answer as CampaignAnswer;
          if (campaignAnswer.campaignId && campaignAnswer.questionId) {
            blockBAnswers.push(campaignAnswer);
          } else {
            // Skip incomplete Block B answers
          }
        } else if (hasBlockAFields) {
          // This is a Block A (curriculum) answer
          const blockAAnswer = answer as BlockAAnswer;
          if (blockAAnswer.questionId && (blockAAnswer.choiceId || blockAAnswer.emojiValue !== undefined)) {
            blockAAnswers[blockAAnswer.questionId] = blockAAnswer;
          } else {
            // Skip incomplete Block A answers
          }
        } else {
          // Unknown answer type; skip
        }
      });

      console.log('[LevelRun] completeReflection:separated', {
        blockAKeys: Object.keys(blockAAnswers),
        blockBCount: blockBAnswers.length,
        blockBQuestions: blockBAnswers.map(a => ({ campaignId: a.campaignId, questionId: a.questionId }))
      });

      // Validate that we have complete Block A answers before submitting
      if (Object.keys(blockAAnswers).length < blockACount) {
        console.error('🐛 Incomplete Block A answers:', { 
          expected: blockACount, 
          actual: Object.keys(blockAAnswers).length,
          answers: blockAAnswers 
        });
        void window.appAlert('Please answer all Block A questions before submitting.');
        setIsLoading(false);
        console.log('[LevelRun] completeReflection:validation failed, isLoading=false');
        return;
      }

      // Submit to backend
      console.log('[LevelRun] completeReflection:submit:calling ProgressService.submitReflection');
      const result = await ProgressService.submitReflection(
        studentId,
        squadId,
        sessionId,
        levelId,
        blockAAnswers,
        blockBAnswers
      );

      console.log('[LevelRun] completeReflection:submit:result', result);
      if (result.success) {
        // Guard against unmounted component
        if (!isMountedRef.current) {
          console.warn('[LevelRun] completeReflection:component unmounted, skipping state updates');
          return;
        }

        setCompletionData({
          blockAEarned: blockACount * 20,
          blockBEarned: blockBCount * 20,
          totalQuestions: blockACount + blockBCount
        });
        console.log('[LevelRun] completeReflection:success -> phase=complete');
        setPhase('complete');
      } else {
        console.error('Failed to submit reflection');
        // Could show error state here
      }
    } catch (error) {
      console.error('Error completing reflection:', error);
      // Could show error state here
    } finally {
      console.log('[LevelRun] completeReflection:finally:done');
      // Guard against unmounted component
      if (isMountedRef.current) {
        setIsLoading(false);
        console.log('[LevelRun] completeReflection:isLoading=false');
      } else {
        console.warn('[LevelRun] completeReflection:component unmounted, skipping setIsLoading(false)');
      }
    }
  }, [studentId, squadId, sessionId, levelId]);

  // Auto-skip phases that have no questions to avoid interim screens
  useEffect(() => {
    if (isLoading) return;
    const blockACount = blockATemplate?.questions.length || 0;
    const blockBCount = allBlockBQuestions.length;
    console.log('[LevelRun] autoSkipEffect', { phase, blockACount, blockBCount, isLoading });
    
    if (phase === 'blockA' && blockACount === 0) {
      console.log('[LevelRun] autoSkipEffect:skipping empty blockA');
      setRunState(prev => ({ ...prev, blockACompleted: true, currentQuestionIndex: 0 }));
      if (blockBCount > 0) {
        console.log('[LevelRun] autoSkipEffect -> phase=blockB');
        setPhase('blockB');
      } else {
        console.log('[LevelRun] autoSkipEffect -> calling completeReflection with no questions');
        completeReflection(runState.answers, runState.totalEarned, 0, 0);
      }
    }
    
    if (phase === 'blockB' && blockBCount === 0) {
      console.log('[LevelRun] autoSkipEffect:empty blockB -> completeReflection');
      completeReflection(runState.answers, runState.totalEarned, blockACount, 0);
    }
  }, [phase, blockATemplate, allBlockBQuestions.length, isLoading, completeReflection, runState.answers, runState.totalEarned]);

  const handleCashAwarded = (_amount: number) => {
    // This is called when the question card plays its animation
    // The actual MuseCash is handled by the answer handlers
  };

  const getCurrentQuestion = () => {
    if (phase === 'blockA' && blockATemplate) {
      return blockATemplate.questions[runState.currentQuestionIndex];
    } else if (phase === 'blockB') {
      return allBlockBQuestions[runState.currentQuestionIndex];
    }
    return null;
  };

  const getProgress = () => {
    if (phase === 'blockA' && blockATemplate) {
      return {
        current: runState.currentQuestionIndex + 1,
        total: blockATemplate.questions.length,
        phase: 'Progression Questions'
      };
    } else if (phase === 'blockB') {
      return {
        current: runState.currentQuestionIndex + 1,
        total: allBlockBQuestions.length,
        phase: 'Encore! 👀 Sneak Peek Bonus Questions'
      };
    }
    return { current: 0, total: 0, phase: '' };
  };

  const currentQuestion = getCurrentQuestion();
  const progress = getProgress();
  console.log('[LevelRun] render', { phase, currentIndex: runState.currentQuestionIndex, totalEarned: runState.totalEarned, isLoading, isProcessing, hasCurrentQuestion: !!currentQuestion });

  if (phase === 'complete' && completionData) {
    return (
      <div className="completion-screen">
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            borderRadius: '2rem',
            padding: '3rem',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(20px)',
            textAlign: 'center',
            maxWidth: '600px',
            margin: '0 auto',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Success Icon */}
          <div
            style={{
              fontSize: '4rem',
              marginBottom: '1rem',
              animation: 'bounceIn 1s ease-out'
            }}
          >
            🎉
          </div>
          
          {/* Title */}
          <h2
            style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              color: '#2C3E50',
              marginBottom: '1rem',
              fontFamily: 'Fredoka One, Arial, sans-serif'
            }}
          >
            Progression Complete!
          </h2>
          
          {/* Summary */}
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
              borderRadius: '1rem',
              padding: '1.5rem',
              margin: '2rem 0',
              border: '2px solid rgba(76, 175, 80, 0.2)'
            }}
          >
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2C3E50', marginBottom: '1rem' }}>
              You earned
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <span style={{ fontSize: '3rem' }}>💰</span>
              <span style={{ fontSize: '3rem', fontWeight: 'bold', color: '#FFD700', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
                +{completionData.blockAEarned + completionData.blockBEarned}
              </span>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '1rem' }}>
              {completionData.blockAEarned > 0 && (
                <div>
                  <div style={{ fontSize: '0.9rem', color: '#546E7A', marginBottom: '0.5rem' }}>
                    Progression Questions
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4FC3F7' }}>
                    +{completionData.blockAEarned} 💰
                  </div>
                </div>
              )}
              
              {completionData.blockBEarned > 0 && (
                <div>
                  <div style={{ fontSize: '0.9rem', color: '#546E7A', marginBottom: '0.5rem' }}>
                    Encore! 👀👀
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#9C27B0' }}>
                    +{completionData.blockBEarned} 💰
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Questions Summary */}
          <div style={{ fontSize: '1rem', color: '#546E7A', marginBottom: '2rem' }}>
            Answered {completionData.totalQuestions} question{completionData.totalQuestions === 1 ? '' : 's'}
          </div>
          
          {/* Action Button */}
          <button
            onClick={() => onComplete(completionData.blockAEarned + completionData.blockBEarned)}
            style={{
              padding: '1rem 2rem',
              background: 'linear-gradient(135deg, #4FC3F7 0%, #45B7D1 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '1rem',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: 'Fredoka One, Arial, sans-serif',
              boxShadow: '0 6px 20px rgba(79, 195, 247, 0.4)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(79, 195, 247, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(79, 195, 247, 0.4)';
            }}
          >
            Continue to Progress Map
          </button>
          
          {/* Background decorations */}
          <div
            style={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(76, 175, 80, 0.1) 0%, transparent 70%)',
              pointerEvents: 'none',
              animation: 'rotate 10s linear infinite'
            }}
          />
        </div>
      </div>
    );
  }

  if (!currentQuestion || isProcessing) {
    // Show spinner during processing
    console.log('[LevelRun] render:processing or no currentQuestion', { isProcessing, hasCurrentQuestion: !!currentQuestion });
    return (
      <div className="level-run-container">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            textAlign: 'center'
          }}
        >
          <div
            style={{
              fontSize: '3rem',
              marginBottom: '1rem',
              animation: 'spin 1s linear infinite'
            }}
          >
            🎵
          </div>
          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2C3E50' }}>
            Processing...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="level-run-container">
      {/* Header with progress */}
      <div
        style={{
          background: 'linear-gradient(135deg, rgba(79, 195, 247, 0.9) 0%, rgba(162, 89, 230, 0.9) 100%)',
          borderRadius: '1rem',
          padding: '1.5rem',
          marginBottom: '2rem',
          color: '#fff',
          boxShadow: '0 8px 25px rgba(79, 195, 247, 0.3)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              Level {levelId}
            </div>
            <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
              {progress.phase}
            </div>
          </div>
          
          <button
            onClick={onCancel}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '0.5rem',
              padding: '0.5rem 1rem',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            Cancel
          </button>
        </div>
        
        {/* Progress Bar */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.9rem' }}>
              Question {progress.current} of {progress.total}
            </span>
            <span style={{ fontSize: '0.9rem' }}>
              💰 {runState.totalEarned} earned
            </span>
          </div>
          
          <div
            style={{
              width: '100%',
              height: '8px',
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '4px',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                width: `${(progress.current / progress.total) * 100}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #FFD700 0%, #FFA500 100%)',
                borderRadius: '4px',
                transition: 'width 0.5s ease',
                boxShadow: '0 0 10px rgba(255, 215, 0, 0.6)'
              }}
            />
          </div>
        </div>
      </div>
      
      {/* Question */}
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <QuestionCard
          question={currentQuestion}
          onAnswer={phase === 'blockA' 
            ? (answer) => handleBlockAAnswer(answer as BlockAAnswer)
            : (answer) => handleBlockBAnswer(answer as CampaignAnswer)
          }
          onCashAwarded={handleCashAwarded}
          disabled={isLoading}
          campaignId={phase === 'blockB' ? (currentQuestion as any).campaignId : undefined}
        />
      </div>
      
      {/* Loading overlay */}
      {isLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '1rem',
              padding: '2rem',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)'
            }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '1rem', animation: 'spin 1s linear infinite' }}>
              🎵
            </div>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2C3E50' }}>
              Saving your reflection...
            </div>
          </div>
        </div>
      )}
      
      {/* Cash Burst */}
      <CashBurst
        amount={20}
        trigger={showCashBurst}
        onComplete={() => setShowCashBurst(false)}
        centerX={50}
        centerY={10}
      />
      
      {/* Additional CSS */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            opacity: 1;
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default LevelRun;